import { Injectable } from '@angular/core';
import { StakeholderWebService } from './stakeholder-web.service';
import {
  Filters,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import { firstValueFrom, Observable, of } from 'rxjs';
import { UtilityService } from '../utility/utility.service';

export interface ModalitaCompilazione {
  id: 'INVIAMAIL' | 'MANUALE';
  descrizione: 'Email' | 'Manuale';
}

export interface Tipologie {
  id: 'ESTERNI' | 'INTERNI';
  descrizione: 'Esterno' | 'Interno';
}

export interface Categorie {
  descrizione: string;
  id: string;
  idAzienda?: string;
  tipologia: string;
  titolo: string;
}

export interface CategoriaStakeholders extends Categorie {
  stakeholders: Stakeholder[];
  peso?: number;
  idAzienda?: string;
  sceltaCampione?: string;
  pesoAssoluto?: number | 0;
}

export interface Stakeholder {
  id: string;
  nome: string;
  cognome: string;
  modalitaCompilazione: string;
  email: string;
  tipologia: string;
  categoria: Categorie;
  idAzienda: string;
  selected?: boolean;
  ragSoc?: string;
  peso?: number;
  pesoAssoluto?: number;
}

@Injectable({
  providedIn: 'root',
})
export class StakeholderService {
  constructor(
    private utilityService: UtilityService,
    private readonly stakeholderWebService: StakeholderWebService,
  ) {}

  /**
   * Funzione per selezionare le righe da far vedere nella grid
   * @param page pagina
   * @param pageSize record per pagina
   * @param ricerca risultato per ricerca
   * @param filters valori da filtrare
   * @param sortBy sort asc desc
   * @returns lista di righe
   */
  public getStakeholder(
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.stakeholderWebService.get(page, pageSize, ricerca, filters, sortBy);
  }

  /**
   * Funzine ws per la creazione del Tematica
   * @param data dati da inserire nel MongoDB
   * @returns chiama la funzione post Stakeholder per la creazione Tematica
   */
  public postStakeholder(data: any) {
    return this.stakeholderWebService.post(data);
  }

  /**
   * Funzione per eliminare una riga da una lista
   * @param idRiga id della riga da eliminare
   * @returns
   */
  public deleteStakeholder(idRiga: any) {
    return this.stakeholderWebService.delete(idRiga);
  }

  /**
   * Stakeholder
   * @param id
   * @param stakeholder
   * @returns
   */
  public putStakeholder(id: string, stakeholder: any) {
    return this.stakeholderWebService.put(id, stakeholder);
  }

  public getStakeMate(id: string, tipologia: 'ESTERNI' | 'INTERNI') {
    return this.stakeholderWebService.getStakeMate(id, tipologia);
  }

  /************ CATEGORIE ***********/

  /**
   * Funzione per selezionare le righe da far vedere nella grid
   * @param page pagina
   * @param pageSize record per pagina
   * @param ricerca risultato per ricerca
   * @param filters valori da filtrare
   * @param sortBy sort asc desc
   * @returns lista di righe
   */
  public getCategorieStake(
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.stakeholderWebService.getCate(page, pageSize, ricerca, filters, sortBy);
  }
  /**
   * Funzione per selezionare le righe da far vedere nella grid Cfg Categorie
   * @param page pagina
   * @param pageSize record per pagina
   * @param ricerca risultato per ricerca
   * @param filters valori da filtrare
   * @param sortBy sort asc desc
   * @returns lista di righe
   */
  public getCategorieStakeCfg(
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.stakeholderWebService.getCateCfg(page, pageSize, ricerca, filters, sortBy);
  }

  /**
   * Funzine ws per la creazione del Tematica
   * @param data dati da inserire nel MongoDB
   * @returns chiama la funzione post Stakeholder per la creazione Tematica
   */
  public postCategorieStake(data: any) {
    return this.stakeholderWebService.postCate(data);
  }

  /**
   * Funzione per eliminare una riga da una lista
   * @param idRiga id della riga da eliminare
   * @returns
   */
  public deleteCategorieStake(idRiga: any) {
    return this.stakeholderWebService.deleteCate(idRiga);
  }

  /**
   * Stakeholder
   * @param id
   * @param stakeholder
   * @returns
   */
  public putCategorieStake(id: string, stakeholder: any) {
    return this.stakeholderWebService.putCate(id, stakeholder);
  }

  public getTipologia(): Observable<Tipologie[]> {
    return of([
      {
        id: 'INTERNI',
        descrizione: 'Interno',
      },
      {
        id: 'ESTERNI',
        descrizione: 'Esterno',
      },
    ]);
  }

  public getStatus(): Observable<any[]> {
    return of([
      {
        id: 'INV',
        descrizione: 'Inviate',
      },
      {
        id: 'NONINV',
        descrizione: 'Non Inviate',
      },
      {
        id: 'NONCOMP',
        descrizione: 'Non Compilate',
      },
      {
        id: 'COMP',
        descrizione: 'Compilate',
      },
    ]);
  }

  public getStatusValidazione(): Observable<any[]> {
    return of([
      {
        id: 'NONCOMP',
        descrizione: 'Non compilato',
      },
      {
        id: 'VERDE',
        descrizione: 'Validato',
      },
      {
        id: 'GIALLO',
        descrizione: 'In attesa di validazione',
      },
      {
        id: 'ROSSO',
        descrizione: 'Non validato',
      },
    ]);
  }

  /************************************************ STAKEHOLDER MATERIALITA */

  public getModalitaCompilazione(): Observable<ModalitaCompilazione[]> {
    return of([
      {
        id: 'INVIAMAIL',
        descrizione: 'Email',
      },
      {
        id: 'MANUALE',
        descrizione: 'Manuale',
      },
    ]);
  }

  /**
   * Riceve in ingresso un'array di categorie stakeholder, con all'interno un array di stakeholders selezionati,
   * e l'array delle categorie con all'interno gli stakeholders già presenti in materialità.
   * Unisce ai dati selezionati quelli presenti in materialità, e ritorna l'array rimappato con le informazioni
   * nel formato della meterialità.
   *
   * @param catStakeSelezionati
   * @returns
   */
  public mergeStakeholdersMaterialita(
    catStakeSelezionati: CategoriaStakeholders[],
    catStakeMaterialita: any[],
  ) {
    return catStakeSelezionati.map((categoria) => {
      const catDaMaterialita = catStakeMaterialita.find(
        (catMat: any) => catMat.id === categoria.id,
      );

      if (catDaMaterialita) {
        categoria.stakeholders = categoria.stakeholders.map((stake) => {
          const stakeDaMaterialita = catDaMaterialita.stakeholders.find(
            (matStak: any) => matStak.id === stake.id,
          );
          if (stakeDaMaterialita) {
            if (stake?.pesoAssoluto) {
              stakeDaMaterialita.pesoAssoluto = stake.pesoAssoluto;
            }
            return stakeDaMaterialita;
          } else {
            return stake;
          }
        });
      }

      return categoria;
    });
  }

  async checkValiditàStakeholderSelezionati(
    stakeholdersMaterialita: any[],
    stakeholdersSelezionati: any[],
    mostraAlertCatSenzaStake: boolean,
  ): Promise<boolean> {
    const selezionati = this.mergeStakeholdersMaterialita(
      this.getValoriSelezionati(stakeholdersSelezionati),
      stakeholdersMaterialita,
    );

    if (!selezionati.length || !selezionati[0].stakeholders.length) {
      this.utilityService.openSnackBar('Non hai selezionato nessuno Stakeholder', '', {
        duration: 2000,
        panelClass: ['red-snackbar'],
      });
      return false;
    }

    // Controllo se è stato selezionato almeno un sh per categoria
    const categorieNonSelezionate: string[] = this._ctrShInCategoria(stakeholdersSelezionati);

    if (categorieNonSelezionate.length && mostraAlertCatSenzaStake) {
      const categorieListString = categorieNonSelezionate.join('<li>');

      const dialog = await this.utilityService.openDialog({
        titolo: 'Attenzione',
        descrizione: `Sono presenti categorie aziendali senza uno stakeholder selezionato:<br><br><li>${categorieListString}<br><br>Vuoi proseguire?`,
        bottoni: [
          {
            nome_btn: 'No',
            id_btn: 'N',
          },
          {
            nome_btn: 'Si',
            id_btn: 'S',
          },
        ],
      });

      const valDialog = await firstValueFrom(dialog.beforeClosed());
      if (valDialog === 'N') {
        return false;
      }
    }

    return true;
  }

  public getValoriSelezionati(arrayGroupCateStake: CategoriaStakeholders[]) {
    return arrayGroupCateStake
      .map((categoria) => {
        return Object.assign({}, categoria, {
          stakeholders: categoria.stakeholders.filter((st) => st.selected),
        });
      })
      .filter((cat) => cat.peso || cat.stakeholders?.length);
  }

  private _ctrShInCategoria(arraySH: any): string[] {
    let categorieNonSelezionate: string[] = [];
    for (const categoria of arraySH) {
      if (Array.isArray(categoria.stakeholders)) {
        if (categoria.idAzienda) {
          if (categoria.stakeholders.length > 0) {
            if (categoria.stakeholders.some((sh: any) => sh.selected)) {
              continue;
            }
          }
          categorieNonSelezionate.push(categoria.titolo);
        }
      }
    }
    return categorieNonSelezionate;
  }
}
